import { useCallback, useRef, useState } from 'react';
import type {
  CustomerProfileView,
  HotelLoyaltyPrograms,
  TripView,
} from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { Button } from '@web/ui/core/atoms';
import type { CardCustomerProfileState } from '@web/ui/core/hooks';
import { EmptyHint } from '@web/ui/core/molecules';
import { CustomerCompanyPanel, CustomerProfilePanel } from '@web/ui/core/organisms';

import { useTravelerTrips } from '../hooks/use_traveler_trips';
import { SelectedTripSection } from './selected_trip_section';
import { TravelerTripsTable } from './traveler_trips_table';

export type TravelerAccess = 'loading' | 'none' | 'forbidden' | 'error' | 'ready';

const NOTICE_KEY = {
  loading: 'travelerView.loading',
  none: 'travelerView.noTraveler',
  forbidden: 'travelerView.noAccess',
  error: 'travelerView.loadError',
} as const;

/** The right half: the trip on top, documents and preferences, every trip, the company — or why they are missing. */
export function TravelerSide({
  access,
  conversationId,
  customerId,
  profileState,
  conversationTripId,
}: Readonly<{
  access: TravelerAccess;
  conversationId: string;
  customerId: string | undefined;
  profileState: CardCustomerProfileState;
  conversationTripId: string | undefined;
}>): React.JSX.Element {
  if (access !== 'ready' || customerId === undefined) {
    return (
      <div className="px-5 py-5">
        <EmptyHint testId="traveler-side-notice">
          {t(NOTICE_KEY[access === 'ready' ? 'loading' : access])}
        </EmptyHint>
      </div>
    );
  }
  return (
    <TravelerSideReady
      conversationId={conversationId}
      customerId={customerId}
      profileState={profileState}
      conversationTripId={conversationTripId}
    />
  );
}

function TravelerSideReady({
  conversationId,
  customerId,
  profileState,
  conversationTripId,
}: Readonly<{
  conversationId: string;
  customerId: string;
  profileState: CardCustomerProfileState;
  conversationTripId: string | undefined;
}>): React.JSX.Element {
  // `null` = nothing picked yet: the conversation's own trip shows.
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const tripsQuery = useTravelerTrips(customerId);
  const topRef = useRef<HTMLDivElement>(null);
  const selectedTrip =
    selectedTripId === null
      ? undefined
      : tripsQuery.data?.find((trip) => trip.id === selectedTripId);
  // Stable, so the trips table keeps its columns (and its cells) across renders.
  const select = useCallback((trip: TripView): void => {
    setSelectedTripId(trip.id);
    // Only the right half scrolls back to the top: `scrollIntoView` would also
    // scroll every ancestor, the document included, and hide the header.
    const scroller = topRef.current?.parentElement;
    if (scroller) scroller.scrollTop = 0;
  }, []);
  return (
    <div ref={topRef} className="flex flex-col gap-5 px-5 py-5">
      <SelectedTripSection
        conversationId={conversationId}
        selectedTrip={selectedTrip}
        conversationTripId={conversationTripId}
      />
      <TravelerDocsAndPrefs state={profileState} />
      <TravelerTripsTable
        query={tripsQuery}
        conversationTripId={conversationTripId}
        activeTripId={selectedTripId ?? conversationTripId ?? null}
        onSelect={select}
      />
      {profileState.status === 'ready' && profileState.company ? (
        <CustomerCompanyPanel company={profileState.company} />
      ) : null}
      {profileState.status === 'ready' ? (
        <FullProfileDisclosure
          profile={profileState.profile}
          hotelLoyalty={profileState.hotelLoyalty}
        />
      ) : null}
    </div>
  );
}

function TravelerDocsAndPrefs({
  state,
}: Readonly<{ state: CardCustomerProfileState }>): React.JSX.Element {
  if (state.status === 'loading') {
    return (
      <p className="m-0 text-meta text-muted-foreground">{t('travelerView.profile.loading')}</p>
    );
  }
  if (state.status === 'unavailable') {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="m-0 text-meta text-tone-red-fg">{t('travelerView.profile.unavailable')}</p>
        {state.retry ? (
          <Button variant="outline" size="sm" onClick={state.retry}>
            {t('card.trip.retry')}
          </Button>
        ) : null}
      </div>
    );
  }
  return (
    <div className="grid gap-4 2xl:grid-cols-2">
      <CustomerProfilePanel profile={state.profile} sections={['travelDocs']} oneColumn />
      <CustomerProfilePanel
        profile={state.profile}
        hotelLoyalty={state.hotelLoyalty}
        sections={['preferences']}
        oneColumn
      />
    </div>
  );
}

/** The whole profile (the customer screen's panel), collapsed by default. */
function FullProfileDisclosure({
  profile,
  hotelLoyalty,
}: Readonly<{
  profile: CustomerProfileView;
  hotelLoyalty: HotelLoyaltyPrograms | null;
}>): React.JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button
          variant="outline"
          size="sm"
          aria-expanded={open}
          onClick={() => {
            setOpen((v) => !v);
          }}
        >
          {open ? t('travelerView.profile.hideFull') : t('travelerView.profile.showFull')}
        </Button>
      </div>
      {open ? <CustomerProfilePanel profile={profile} hotelLoyalty={hotelLoyalty} /> : null}
    </div>
  );
}
